import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type { ClaireRegressionRunsReport } from '@borradh-workspace/api-client/types';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

export const fetchClaireRegressions = (
  options: { refresh?: boolean } = {},
  signal?: AbortSignal
) =>
  apiClient.get<ClaireRegressionRunsReport>(
    `admin-terminal/claire-regressions${options.refresh ? '?refresh=true' : ''}`,
    { signal }
  );

export const claireRegressionsQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.adminTerminal.claireRegressions(),
    queryFn: ({ signal }) => fetchClaireRegressions({}, signal),
    // The API caches Langfuse for five minutes, so asking more often only
    // re-reads its cache: a run still being scored is re-checked on that beat.
    staleTime: 60_000,
    refetchInterval: (query) =>
      query.state.data?.runs.some((r) => r.status === 'scoring')
        ? 5 * 60_000
        : false,
  });

export const useClaireRegressions = () =>
  useQuery(claireRegressionsQueryOptions());

/** Re-read Langfuse now, past the API's cache. */
export const useRefreshClaireRegressions = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => fetchClaireRegressions({ refresh: true }),
    onSuccess: (report) =>
      queryClient.setQueryData(
        queryKeys.adminTerminal.claireRegressions(),
        report
      ),
    onError: (error: Error) =>
      toast.error(error.message || 'Could not refresh the regression runs'),
  });
  return { refresh: mutation.mutate, isRefreshing: mutation.isPending };
};
